import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import { annotateHtml } from '../utils/annotateHtml';
import { describeSelection, reanchor } from '../utils/textAnchor';
import AnnotationTooltip from './AnnotationTooltip';
import './TranslationComments.css';

const HIGHLIGHT_NAME = 'translation-comment';
const HIGHLIGHT_ACTIVE = 'translation-comment-active';
const supportsHighlight =
  typeof window !== 'undefined' &&
  !!window.CSS &&
  !!window.CSS.highlights &&
  typeof window.Highlight === 'function';

function AnnotatedTranslation({
  html,
  annotations,
  translatedLines,
  sourceLang,
  targetLang,
  documentId = null,
  enableComments = false,
}) {
  const { t } = useTranslation('chat');
  const containerRef = useRef(null);
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const hideTimeoutRef = useRef(null);

  // Inline comments: fragments the reviewer selected + notes attached to them.
  const [comments, setComments] = useState([]);
  const [pending, setPending] = useState(null); // { quote, prefix, suffix, start, end, rect }
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const composingRef = useRef(false);
  const rangesRef = useRef({}); // comment id -> live Range (for scroll/flash)

  const setComposingSafe = useCallback((v) => {
    composingRef.current = v;
    setComposing(v);
  }, []);

  const annotatedHtml = useMemo(
    () => annotateHtml(html, translatedLines, annotations),
    [html, translatedLines, annotations]
  );

  const handleMouseEnter = useCallback((e) => {
    const span = e.target.closest('.annotation');
    if (!span) return;

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const idx = parseInt(span.dataset.annotationIdx, 10);
    if (isNaN(idx) || !annotations || !annotations[idx]) return;

    const rect = span.getBoundingClientRect();
    const x = rect.left + rect.width / 2;

    // Position above the span, or below if near the top
    const spaceAbove = rect.top;
    const y = spaceAbove > 150 ? rect.top : rect.bottom + 8;

    setActiveAnnotation(annotations[idx]);
    setTooltipPosition({ x, y });
  }, [annotations]);

  const handleMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveAnnotation(null);
      setTooltipPosition(null);
    }, 200);
  }, []);

  // Keep the tooltip open while the user moves into it (e.g. to click the
  // dictionary link in the footer). Without this, the brief hide-timeout from
  // the annotation's mouseleave fires before the click registers.
  const handleTooltipMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveAnnotation(null);
      setTooltipPosition(null);
    }, 100);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clean up DOCX conversion artifacts:
    // 1. Hide visually empty paragraphs
    container.querySelectorAll('p').forEach(p => {
      const text = p.textContent.replace(/ /g, '').trim();
      if (!text) {
        p.style.display = 'none';
        p.style.margin = '0';
      }
    });
    // 2. Collapse whitespace-only spans with large font-size (DOCX trailing spaces)
    container.querySelectorAll('span[style]').forEach(span => {
      const text = span.textContent.replace(/ /g, '').trim();
      if (!text && span.style.fontSize) {
        span.style.fontSize = '0';
        span.style.lineHeight = '0';
      }
    });

    // Attach tooltip handlers to annotation spans
    const spans = container.querySelectorAll('.annotation');
    spans.forEach(span => {
      span.addEventListener('mouseenter', handleMouseEnter);
      span.addEventListener('mouseleave', handleMouseLeave);
    });

    return () => {
      spans.forEach(span => {
        span.removeEventListener('mouseenter', handleMouseEnter);
        span.removeEventListener('mouseleave', handleMouseLeave);
      });
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [annotatedHtml, handleMouseEnter, handleMouseLeave]);

  // Load this document's comments once.
  useEffect(() => {
    if (!enableComments || !documentId) return undefined;
    let cancelled = false;
    api.listTranslationComments(documentId)
      .then((data) => { if (!cancelled) setComments(data); })
      .catch((err) => console.error('Failed to load translation comments:', err));
    return () => { cancelled = true; };
  }, [enableComments, documentId]);

  // (Re-)paint highlights for every anchored comment. Re-runs when the rendered
  // text or the comment set changes.
  useEffect(() => {
    if (!enableComments || !supportsHighlight) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const ranges = {};
    const highlight = new window.Highlight();
    comments.forEach((c) => {
      const range = reanchor(container, c);
      if (range) {
        ranges[c.id] = range;
        highlight.add(range);
      }
    });
    rangesRef.current = ranges;
    if (highlight.size > 0) CSS.highlights.set(HIGHLIGHT_NAME, highlight);
    else CSS.highlights.delete(HIGHLIGHT_NAME);

    return () => {
      CSS.highlights.delete(HIGHLIGHT_NAME);
      CSS.highlights.delete(HIGHLIGHT_ACTIVE);
    };
  }, [comments, annotatedHtml, enableComments]);

  // A finished selection inside the container becomes a pending comment anchor.
  useEffect(() => {
    if (!enableComments) return undefined;
    const onMouseUp = () => {
      if (composingRef.current) return;
      const container = containerRef.current;
      setPending(container ? describeSelection(container) : null);
    };
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, [enableComments]);

  // Drop the floating affordance if the page scrolls out from under it (unless
  // the user is mid-note).
  useEffect(() => {
    if (!pending || composing) return undefined;
    const onScroll = () => setPending(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [pending, composing]);

  const cancelPending = useCallback(() => {
    setPending(null);
    setComposingSafe(false);
    setDraft('');
    window.getSelection()?.removeAllRanges();
  }, [setComposingSafe]);

  const saveComment = useCallback(async () => {
    const text = draft.trim();
    if (!text || !pending) return;
    setSaving(true);
    try {
      const created = await api.createTranslationComment(documentId, pending, text);
      setComments((prev) => [...prev, created]);
      cancelPending();
    } catch (err) {
      console.error('Failed to save translation comment:', err);
      alert(t('error'));
    } finally {
      setSaving(false);
    }
  }, [draft, pending, documentId, cancelPending, t]);

  const removeComment = useCallback(async (id) => {
    try {
      await api.deleteTranslationComment(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Failed to delete translation comment:', err);
    }
  }, []);

  const scrollToComment = useCallback((id) => {
    const range = rangesRef.current[id];
    if (!range) return;
    range.startContainer.parentElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (supportsHighlight) {
      CSS.highlights.set(HIGHLIGHT_ACTIVE, new window.Highlight(range));
      setTimeout(() => CSS.highlights.delete(HIGHLIGHT_ACTIVE), 1400);
    }
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        className="translation-text html-content"
        dangerouslySetInnerHTML={{ __html: annotatedHtml }}
      />

      {activeAnnotation && (
        <AnnotationTooltip
          annotation={activeAnnotation}
          position={tooltipPosition}
          sourceLang={sourceLang}
          targetLang={targetLang}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}

      {enableComments && pending && !composing && createPortal(
        <button
          type="button"
          className="tc-add-btn"
          style={{ top: pending.rect.top - 8, left: pending.rect.left + pending.rect.width / 2 }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setComposingSafe(true)}
        >
          <MessageSquarePlus size={15} />
          {t('comments.add')}
        </button>,
        document.body
      )}

      {enableComments && pending && composing && createPortal(
        <div
          className="tc-popover"
          style={{ top: pending.rect.bottom + 8, left: pending.rect.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="tc-popover-quote">“{pending.quote}”</div>
          <textarea
            className="tc-popover-input"
            autoFocus
            rows={3}
            placeholder={t('comments.placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveComment();
              if (e.key === 'Escape') cancelPending();
            }}
          />
          <div className="tc-popover-actions">
            <button type="button" className="tc-btn-ghost" onClick={cancelPending}>
              {t('comments.cancel')}
            </button>
            <button
              type="button"
              className="tc-btn-primary"
              onClick={saveComment}
              disabled={saving || !draft.trim()}
            >
              {saving ? t('comments.saving') : t('comments.save')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {enableComments && comments.length > 0 && (
        <div className="tc-list">
          <div className="tc-list-title">
            {t('comments.listTitle')} ({comments.length})
          </div>
          {comments.map((c) => (
            <div key={c.id} className="tc-list-item">
              <button
                type="button"
                className="tc-list-quote"
                onClick={() => scrollToComment(c.id)}
                title={t('comments.jump')}
              >
                “{c.quote}”
              </button>
              <div className="tc-list-note">{c.comment}</div>
              <button
                type="button"
                className="tc-list-delete"
                onClick={() => removeComment(c.id)}
                title={t('comments.delete')}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default AnnotatedTranslation;
